import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, ArrowRight, Loader2, Flame } from "lucide-react";
import { adminApi, ADMIN_PROJECTS } from "@/lib/adminApi";
import { toast } from "sonner";

/**
 * AdminDashboard — Today view across all projects.
 * Shows: today's date, open tasks for today (grouped by project), current Q goals,
 * and a quick-capture box.
 */
export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [streak, setStreak] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [capText, setCapText] = useState("");
  const [capProject, setCapProject] = useState(ADMIN_PROJECTS[0].slug);
  const [capping, setCapping] = useState(false);

  useEffect(() => {
    adminApi.getToday().then((r) => setData(r.data));
    adminApi.getStreak().then((r) => setStreak(r.data));
  }, [refresh]);

  const onQuickCapture = async (e) => {
    e.preventDefault();
    if (!capText.trim()) return;
    setCapping(true);
    try {
      await adminApi.quickCapture(capProject, capText.trim());
      setCapText("");
      toast.success("Captured");
      setRefresh((x) => x + 1);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCapping(false);
    }
  };

  const onToggleTask = async (t) => {
    const optimistic = {
      ...data,
      day_tasks: data.day_tasks.map((x) => (x.id === t.id ? { ...x, done: !x.done } : x)),
    };
    setData(optimistic);
    try {
      await adminApi.patchTask(t.id, { done: !t.done });
    } catch {
      setRefresh((x) => x + 1);
    }
  };

  const projectName = (slug) =>
    ADMIN_PROJECTS.find((p) => p.slug === slug)?.name || slug;

  if (!data) {
    return (
      <div className="flex items-center gap-3 opacity-60 text-sm">
        <Loader2 size={14} className="animate-spin" /> loading today…
      </div>
    );
  }

  // group day_tasks by project_slug
  const byProject = data.day_tasks.reduce((acc, t) => {
    (acc[t.project_slug] = acc[t.project_slug] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="space-y-12" data-testid="admin-dashboard">
      <div>
        <p className="text-xs tracking-widest opacity-60 mb-2">TODAY</p>
        <h1 className="font-serif text-4xl">
          {new Date().toLocaleDateString(undefined, {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        </h1>
        <div className="flex items-center gap-4 mt-3">
          <p className="text-xs tracking-widest opacity-60">
            {data.quarter_key} · {data.month_key}
          </p>
          {streak && streak.streak > 0 && (
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs tracking-widest border ${
                streak.today_done
                  ? "border-four-yellow text-four-yellow"
                  : "border-parchment-faint text-parchment-dusk"
              }`}
              data-testid="streak-badge"
              title={
                streak.today_done
                  ? `You've journaled ${streak.streak} day${streak.streak === 1 ? "" : "s"} in a row.`
                  : `${streak.streak}-day streak — don't break it! Write a diary entry today.`
              }
            >
              <Flame size={12} />
              {streak.streak} DAY{streak.streak === 1 ? "" : "S"}
              {!streak.today_done && <span className="opacity-60 ml-1">· not yet today</span>}
            </span>
          )}
        </div>
      </div>

      {/* Quick capture */}
      <div className="border border-parchment-faint p-5">
        <p className="text-xs tracking-widest opacity-60 mb-3 text-four-yellow flex items-center gap-2">
          <Zap size={12} /> QUICK CAPTURE
        </p>
        <form onSubmit={onQuickCapture} className="flex items-center gap-3">
          <select
            value={capProject}
            onChange={(e) => setCapProject(e.target.value)}
            className="bg-ink border border-parchment-faint px-2 py-2 text-sm outline-none"
            data-testid="quick-capture-project"
          >
            {ADMIN_PROJECTS.map((p) => (
              <option key={p.slug} value={p.slug}>
                {p.name}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={capText}
            onChange={(e) => setCapText(e.target.value)}
            placeholder="What needs to happen today?"
            className="flex-1 bg-transparent border-b border-parchment-faint focus:border-four-yellow outline-none text-sm py-2 placeholder:opacity-30"
            data-testid="quick-capture-input"
          />
          <button
            type="submit"
            disabled={capping || !capText.trim()}
            className="px-4 py-2 border border-parchment-faint hover:border-four-yellow hover:text-four-yellow tracking-widest text-xs transition-colors disabled:opacity-30"
            data-testid="quick-capture-submit"
          >
            ADD
          </button>
        </form>
      </div>

      {/* Tasks by project */}
      <div>
        <p className="text-xs tracking-widest opacity-60 mb-4">TASKS · {data.date}</p>
        {Object.keys(byProject).length === 0 && (
          <p className="text-sm opacity-40 italic">Nothing scheduled for today.</p>
        )}
        <div className="space-y-6">
          {Object.entries(byProject).map(([slug, tasks]) => (
            <div key={slug} data-testid={`today-tasks-${slug}`}>
              <Link
                to={`/admin/planning/${slug}`}
                className="text-xs tracking-widest text-four-yellow inline-flex items-center gap-2 hover:underline mb-2"
              >
                {projectName(slug).toUpperCase()} <ArrowRight size={10} />
              </Link>
              <ul className="space-y-1">
                {tasks.map((t) => (
                  <li key={t.id} className="flex items-center gap-3 py-1">
                    <button
                      onClick={() => onToggleTask(t)}
                      className={`w-4 h-4 border flex-shrink-0 ${
                        t.done
                          ? "border-four-yellow bg-four-yellow"
                          : "border-parchment-faint hover:border-four-yellow"
                      }`}
                      data-testid={`today-task-toggle-${t.id}`}
                    />
                    <span className={`text-sm ${t.done ? "line-through opacity-40" : ""}`}>
                      {t.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Current Q goals */}
      <div>
        <p className="text-xs tracking-widest opacity-60 mb-4">
          OPEN GOALS · {data.quarter_key}
        </p>
        {data.quarter_goals.length === 0 && (
          <p className="text-sm opacity-40 italic">No open quarterly goals yet.</p>
        )}
        <ul className="space-y-2">
          {data.quarter_goals.map((g) => (
            <li
              key={g.id}
              className="flex items-center gap-3 text-sm"
              data-testid={`q-goal-${g.id}`}
            >
              <span className="text-xs tracking-widest opacity-60 w-28">
                {projectName(g.project_slug).toUpperCase()}
              </span>
              <span>{g.text}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Project shortcuts */}
      <div>
        <p className="text-xs tracking-widest opacity-60 mb-4">PLANNING</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {ADMIN_PROJECTS.map((p) => (
            <Link
              key={p.slug}
              to={`/admin/planning/${p.slug}`}
              className="px-4 py-4 border border-parchment-faint hover:border-four-yellow hover:text-four-yellow text-sm tracking-widest transition-colors flex items-center justify-between"
              data-testid={`project-link-${p.slug}`}
            >
              {p.name.toUpperCase()}
              <ArrowRight size={14} />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
